import React, { useEffect, useState } from "react";
import { Modal, Button, Spinner } from "react-bootstrap";
import ApiService from "../../../_services/ApiService";

export interface RecordedVideoResource {
  id: number;
  name: string;
  url: string;
  type: string | null;     // 'youtube' | 'vimeo' | 'sharepoint' | null
  typeName: string | null;
  typeId: number | null;
}

interface Props {
  show: boolean;
  onClose: () => void;
  activitySlotTemplateId: number | null;
  batchId?: number | null;
  topicName?: string | null;
}

/**
 * Recorded Video modal — Node port of the Java "Recorded Video" popup driven by
 * /api/common/timetable/student/viewVideos. On open, fetches study resources
 * mapped to the activity slot template and renders an inline player when the URL
 * is recognizable (YouTube / Vimeo) or a launch link otherwise.
 */
const RecordedVideoModal: React.FC<Props> = ({
  show,
  onClose,
  activitySlotTemplateId,
  batchId,
  topicName,
}) => {
  const [loading, setLoading] = useState(false);
  const [videos, setVideos] = useState<RecordedVideoResource[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [selectedIdx, setSelectedIdx] = useState(0);

  useEffect(() => {
    if (!show || !activitySlotTemplateId) return;
    let cancelled = false;
    setLoading(true);
    setVideos([]);
    setErrorMessage(null);
    setSelectedIdx(0);
    ApiService.getRecordedVideosForSlot(activitySlotTemplateId, batchId ?? undefined)
      .then((response) => {
        if (cancelled) return;
        const data = response?.data || {};
        if (data.status === 1 && Array.isArray(data.studyResources) && data.studyResources.length > 0) {
          setVideos(data.studyResources);
        } else {
          setErrorMessage(data.message || "No videos available");
        }
      })
      .catch((err) => {
        if (cancelled) return;
        setErrorMessage(err?.message || "Failed to load recorded videos");
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [show, activitySlotTemplateId, batchId]);

  const selected = videos[selectedIdx];

  return (
    <Modal show={show} onHide={onClose} size="lg" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: "1.05em" }}>
          Recorded Video{topicName ? <span style={{ color: "var(--text-muted)", fontWeight: 400 }}> · {topicName}</span> : null}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ minHeight: "260px", padding: "16px" }}>
        {loading && (
          <div style={{ textAlign: "center", padding: "40px 0" }}>
            <Spinner animation="border" variant="primary" />
            <p style={{ marginTop: "10px", color: "var(--text-muted)" }}>
              Looking up recorded videos…
            </p>
          </div>
        )}

        {!loading && errorMessage && (
          <div
            style={{
              padding: "20px",
              textAlign: "center",
              color: "var(--status-danger)",
              background: "var(--status-danger-bg)",
              border: "1px solid var(--status-danger-border)",
              borderRadius: "8px",
              fontSize: "1em",
            }}
          >
            {errorMessage}
          </div>
        )}

        {!loading && !errorMessage && videos.length > 0 && (
          <>
            {videos.length > 1 && (
              <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginBottom: "12px" }}>
                {videos.map((v, idx) => (
                  <button
                    key={v.id}
                    type="button"
                    onClick={() => setSelectedIdx(idx)}
                    style={{
                      padding: "6px 12px",
                      borderRadius: "6px",
                      fontSize: "0.85em",
                      fontWeight: 500,
                      border: idx === selectedIdx ? "1px solid #4f46e5" : "1px solid #d1d5db",
                      background: idx === selectedIdx ? "#eef2ff" : "#fff",
                      color: idx === selectedIdx ? "#4338ca" : "#1f2937",
                      cursor: "pointer",
                    }}
                  >
                    {v.name || `Video ${idx + 1}`}
                  </button>
                ))}
              </div>
            )}
            {selected && <VideoPlayer video={selected} />}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>Close</Button>
      </Modal.Footer>
    </Modal>
  );
};

/* ---------- Video player ---------- */

const VideoPlayer: React.FC<{ video: RecordedVideoResource }> = ({ video }) => {
  const embedUrl = toEmbedUrl(video.url, video.type);

  return (
    <div>
      {embedUrl ? (
        <div
          style={{
            position: "relative",
            paddingTop: "56.25%",
            background: "#000",
            borderRadius: "8px",
            overflow: "hidden",
          }}
        >
          <iframe
            title={video.name || "Recorded Video"}
            src={embedUrl}
            allow="autoplay; fullscreen; picture-in-picture"
            allowFullScreen
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              border: "none",
            }}
          />
        </div>
      ) : (
        <div
          style={{
            padding: "32px",
            textAlign: "center",
            background: "var(--surface-page)",
            border: "1px dashed var(--border-strong)",
            borderRadius: "8px",
          }}
        >
          <p style={{ marginBottom: "12px", color: "var(--text-body)" }}>
            This recording can't be embedded inline. Open it in a new tab.
          </p>
          <a
            href={video.url}
            target="_blank"
            rel="noreferrer"
            style={{
              display: "inline-block",
              padding: "8px 18px",
              background: "var(--brand-primary)",
              color: "var(--text-on-brand)",
              borderRadius: "6px",
              fontWeight: 600,
              textDecoration: "none",
              fontSize: "0.9em",
            }}
          >
            Open Video ↗
          </a>
        </div>
      )}
    </div>
  );
};

/**
 * Convert provider URLs to embed-friendly forms. Returns null when we don't
 * know how to embed (caller renders an "Open Video" link instead).
 */
function toEmbedUrl(url: string, type: string | null): string | null {
  if (!url) return null;
  try {
    if (type === "youtube") {
      const ytId = extractYouTubeId(url);
      if (ytId) return `https://www.youtube.com/embed/${ytId}`;
    }
    if (type === "vimeo") {
      const vimeoId = extractVimeoId(url);
      if (vimeoId) return `https://player.vimeo.com/video/${vimeoId}`;
    }
  } catch {
    // fall through
  }
  // SharePoint personal share links and other unknown types — let user open externally
  return null;
}

function extractYouTubeId(url: string): string | null {
  const patterns = [
    /[?&]v=([\w-]{6,})/,
    /youtu\.be\/([\w-]{6,})/,
    /youtube\.com\/embed\/([\w-]{6,})/,
  ];
  for (const re of patterns) {
    const m = url.match(re);
    if (m) return m[1];
  }
  return null;
}

function extractVimeoId(url: string): string | null {
  const m = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  return m ? m[1] : null;
}

export default RecordedVideoModal;
